// Piezas visuales compartidas del módulo Diagnósticos. Tokens semánticos del tema
// (bg-card, text-foreground, border-border…), nunca colores fijos.
import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

export const PROPOSITO_LABEL: Record<string, string> = {
  interno: 'Interno',
  cliente: 'Clientes',
  captacion: 'Captación',
};
export const PROPOSITO_HINT: Record<string, string> = {
  interno: 'La org se diagnostica a sí misma. Resultados a su memoria/backlog (fase 2).',
  cliente: 'Servicio de consultoría. Cada cliente aislado; nunca se mezcla con la memoria de la org.',
  captacion: 'Regalo a leads: un proyecto por lead; resultados a CRM y email marketing con consentimiento.',
};
export const TIPO_LABEL: Record<string, string> = {
  capacitacion: 'Capacitación',
  implementacion_agente: 'Implementación de agente',
  producto: 'Desarrollo de producto',
  organizacional: 'Diagnóstico organizacional',
};
export const STATUS_LABEL: Record<string, string> = {
  pendiente: 'Pendiente', invitado: 'Invitado', en_conversacion: 'En conversación', completado: 'Completado',
  analizando: 'Analizando', analizado: 'Analizado', error: 'Error',
  borrador: 'Borrador', activo: 'Activo', cerrado: 'Cerrado', pausado: 'Pausado', archivado: 'Archivado',
};
export const ROLE_LABEL: Record<string, string> = { owner: 'Dueño/a', invitado: 'Invitado/a', '': 'Participante' };

export const fmtDate = (iso: string | null | undefined) =>
  iso ? new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(iso)) : '—';

export function Chip({ children, tone = 'muted' }: { children: ReactNode; tone?: 'muted' | 'primary' | 'ok' | 'warn' | 'bad' }) {
  const cls = {
    muted: 'bg-muted text-muted-foreground',
    primary: 'bg-primary/10 text-primary',
    ok: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
    warn: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
    bad: 'bg-destructive/10 text-destructive',
  }[tone];
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}>{children}</span>;
}

export function statusTone(s: string): 'muted' | 'primary' | 'ok' | 'warn' | 'bad' {
  if (s === 'analizado' || s === 'activo' || s === 'released') return 'ok';
  if (s === 'error') return 'bad';
  if (s === 'analizando' || s === 'en_conversacion' || s === 'pausado') return 'warn';
  if (s === 'completado' || s === 'invitado') return 'primary';
  return 'muted';
}

export function Panel({ title, actions, children }: { title?: ReactNode; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-border bg-card p-4 shadow-sm">
      {(title || actions) && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          {title && <h3 className="text-sm font-semibold text-foreground">{title}</h3>}
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function Btn({ children, onClick, kind = 'secondary', disabled, type = 'button', busy }: {
  children: ReactNode; onClick?: () => void; kind?: 'primary' | 'secondary' | 'danger'; disabled?: boolean;
  type?: 'button' | 'submit'; busy?: boolean;
}) {
  const cls = {
    primary: 'bg-primary text-primary-foreground hover:opacity-90',
    secondary: 'border border-border bg-card text-foreground hover:bg-muted',
    danger: 'border border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/20',
  }[kind];
  return (
    <button type={type} onClick={onClick} disabled={disabled || busy}
      className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition disabled:opacity-50 ${cls}`}>
      {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      {children}
    </button>
  );
}

export const inputCls =
  'w-full rounded-lg border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40';

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-foreground">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-muted-foreground">{hint}</span>}
    </label>
  );
}

export function ErrorBox({ error }: { error: string }) {
  if (!error) return null;
  return <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">{children}</div>;
}

export function Spinner() {
  return <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>;
}

/** Render genérico (y legible) de JSON de análisis/reporte: títulos por clave, listas y valores. */
export function DataView({ data, depth = 0 }: { data: unknown; depth?: number }) {
  if (data === null || data === undefined || data === '') return <span className="text-muted-foreground">—</span>;
  if (typeof data !== 'object') return <span className="text-foreground">{String(data)}</span>;
  if (Array.isArray(data)) {
    if (!data.length) return <span className="text-muted-foreground">—</span>;
    return (
      <ul className="list-disc space-y-1 pl-5">
        {data.slice(0, 50).map((x, i) => <li key={i}><DataView data={x} depth={depth + 1} /></li>)}
      </ul>
    );
  }
  const entries = Object.entries(data as Record<string, unknown>).filter(([k]) => !k.startsWith('_') && k !== 'verificacion');
  if (depth > 4) return <code className="text-xs text-muted-foreground">{JSON.stringify(data).slice(0, 300)}</code>;
  return (
    <div className={depth === 0 ? 'space-y-3' : 'space-y-1'}>
      {entries.map(([k, v]) => (
        <div key={k} className={depth === 0 ? '' : 'text-sm'}>
          <div className={depth === 0 ? 'text-xs font-semibold uppercase tracking-wide text-muted-foreground' : 'inline font-medium text-foreground'}>
            {k.replace(/_/g, ' ')}{depth > 0 ? ': ' : ''}
          </div>
          <div className={depth === 0 ? 'mt-1 text-sm' : 'inline'}><DataView data={v} depth={depth + 1} /></div>
        </div>
      ))}
    </div>
  );
}
